import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { AccountInfo, EventMessage, EventType, InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser';

// action - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project import
//import Loader from 'components/Loader';
import { AuthProps, AzureB2cContextType } from 'types/auth';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { loginRequestOptions, msalConfig } from 'b2cAuthConfig';

// const
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| AzURE B2C CONTEXT & PROVIDER ||============================== //

const buildUser = (account: AccountInfo | null | undefined) => {
  if (!account) return null;

  return {
    id: account!.idTokenClaims?.sub,
    email: account!.idTokenClaims?.emails?.[0] ?? '',
    name: account!.name
  };
};

const AzureB2cContext = createContext<AzureB2cContextType | null>(null);

export const AzureB2cContextProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const { instance: msalInstance } = useMsal();
  const { login, error, result } = useMsalAuthentication(InteractionType.Silent, loginRequestOptions);

  const applyLogin = useCallback(
    (account: AccountInfo | null | undefined, token?: string | null) => {
      if (!account) {
        dispatch({ type: LOGOUT });
        return;
      }

      msalInstance.setActiveAccount(account);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: buildUser(account),
          token: token ?? undefined
        }
      });
    },
    [msalInstance]
  );

  useEffect(() => {
    const active = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (active) {
      applyLogin(active);
    } else {
      dispatch({ type: LOGOUT });
    }

    const cbId = msalInstance.addEventCallback((ev: EventMessage) => {
      if (
        ev.eventType === EventType.LOGIN_SUCCESS ||
        ev.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        ev.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        // Prefer account from payload; fallback to active/cached
        const account: AccountInfo | undefined =
          (ev as any)?.payload?.account || msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

        applyLogin(account);
      }

      if (ev.eventType === EventType.LOGIN_FAILURE || ev.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        dispatch({ type: LOGOUT });
      }
    });

    return () => {
      if (cbId) msalInstance.removeEventCallback(cbId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance, applyLogin]);

  // Handle silent auth result + interaction-required fallback
  useEffect(() => {
    // If the silent authentication is successful, then set the active account returned.
    if (!error && !!result?.account) {
      applyLogin(result.account, result.accessToken);

      // If we do not have an active session then we need to redirect and login
    } else if (error instanceof InteractionRequiredAuthError) {
      login(InteractionType.Redirect).then((res) => {
        applyLogin(res?.account ?? null, res?.accessToken ?? null);
      });
    } else if (error) {
      // Non-interaction-required errors should still mark as initialized
      dispatch({ type: LOGOUT });
    }
  }, [dispatch, error, result, login, applyLogin]);

  const logout = () => {
    msalInstance.setActiveAccount(null);
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: msalConfig.auth.redirectUri
    });
    dispatch({ type: LOGOUT });
  };

  return (
    <AzureB2cContext.Provider
      value={{
        ...state,
        token: result?.accessToken,
        login,
        logout
      }}
    >
      {children}
    </AzureB2cContext.Provider>
  );
};

export default AzureB2cContext;
